import { cacheDecorator, updateCache } from '@ds/swymkit/tools'
import { logger } from '@ds/swymkit/utils'
import { defineStore } from 'pinia'

import { getFacet, getFacets } from '~/features-shared/facet/api/facets'
import type { FacetExperience, FacetUnion } from '~/features-shared/facet/interfaces/facet'
import { FacetType } from '~/features-shared/facet/interfaces/facet'
import { createFacetStyles } from '~/features-shared/facet/utils/facets'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

import { useFeatureManager } from '~/features-generic/config-manager/composables/feature-manager'

/**
 * Cache timeout for facet store
 */
const FACET_MAX_CACHE = 60_000 * 60

/**
 * Community Facet Store
 */
export const useCommunityFacetStore = defineStore('community-facets', () => {
  const isGAFeatureActivated = useFeatureManager('community.features.ga_facet')
  const isLAFeatureActivated = useFeatureManager('community.features.la_facet')
  const isFacetFeatureActivated = computed(() => isGAFeatureActivated.value || isLAFeatureActivated.value)

  // states
  const facetByCommunity = reactive(new Map())

  /**
   * @description Set or Updated community facet store
   * @param communityUri
   * @param facetUri
   *
   */
  const setOrUpdateCommunityFacets = (communityUri: SUBJECT_6W_URI, communityFacets: FacetUnion[]) => {
    if (!communityUri)
      return

    facetByCommunity.set(communityUri, communityFacets)
    // setOrUpdate the facet store will update the cache (use case : when saving new facets in community settings, we update the cache)
    updateCache(`get-community-facet-${communityUri}`, communityFacets, FACET_MAX_CACHE)
    communityFacets.map((facet) => {
      if (facet.type === FacetType.EXPERIENCE)
        createFacetStyles(facet)
    })
  }

  /**
   * @description Fetch community facets or Get them if they already where fetched (NOTE: community facets are cache see cache time FACET_MAX_CACHE)
   * @param communityUri
   * @param facetUri
   *
   */
  const fetchOrGetCommunityFacets = async (communityUri: SUBJECT_6W_URI) => {
    try {
      return cacheDecorator(
        `get-community-facet-${communityUri}`,
        async () => {
          const communityFacets = await getFacets(communityUri)
          setOrUpdateCommunityFacets(communityUri, communityFacets)

          if (!isFacetFeatureActivated.value)
            return []

          return communityFacets
        },
        FACET_MAX_CACHE,
      )
    }
    catch (error) {
      logger.error(error)
    }
  }

  /**
   * @description Fetch a specific community facet or Get it
   * @param communityUri
   * @param facetUri
   *
   */
  const fetchOrGetCommunityFacet = async (communityUri: SUBJECT_6W_URI, facetUri: string): Promise<FacetExperience | undefined> => {
    try {
      const facets: FacetUnion[] = await fetchOrGetCommunityFacets(communityUri) || []
      let facetIndex
      const facet = facets.find((facet, index) => {
        if (facet.uri === facetUri) {
          facetIndex = index
          return true
        }
        return false
      }) || undefined

      // here we check if widget preference is undefined, if it is we need to call /facet/[id] to get the widgets preferences
      if (facet && (facet as FacetExperience).widgetPreference === undefined && facetIndex !== undefined) {
        const completeFacetData = await getFacet(communityUri, facetUri)
        if (completeFacetData) {
          facets[facetIndex] = completeFacetData as FacetUnion
          facetByCommunity.set(communityUri, facets)
          return completeFacetData as FacetExperience
        }
        return undefined
      }
      return facet as FacetExperience
    }
    catch (error) {
      logger.error('Could not get facet', error)
      return undefined
    }
  }

  return {
    // states
    facetByCommunity,
    // setter
    setOrUpdateCommunityFacets,

    // action
    fetchOrGetCommunityFacets,
    fetchOrGetCommunityFacet,
  }
})
